import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {assertAllAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import ReorderableSimpleTable, {
  reorderableSimpleTableStyles,
} from 'ui-library/reorderable/ReorderableSimpleTable';
import {Row} from 'ui-library/reorderable/ReorderableSimpleTableConstants';
import * as DateUtil from 'util/DateUtil';

import {ListPlates, ListPlatesVariables} from './__generated/ListPlates';

export interface PlateWorklistItemsProps {
  itemIds: Array<string>;
  onReorderRow: (id: string, newIndex: number) => void;
}

export const LIST_PLATES_QUERY = gql`
  query ListPlates($plateIds: [StrictString]!) @skipAudit @engComponent(name: WORKLISTS) {
    plates(ids: $plateIds) {
      ... on PlateNode {
        id
        name
        modifiedAt
        parentStorable {
          ... on StorableInterface {
            id
            name
          }
          ...InaccessibleInfoGraphQL
        }
        barcode {
          value
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function PlateWorklistItems(props: PlateWorklistItemsProps): JSX.Element {
  function getPlateRows(plates: Array<ListPlates['plates'][number]>): Array<Row> {
    return assertAllAccessible(plates).map((plate) => ({
      id: plate.id,
      cells: [
        {key: `${plate.id}:barcode`, contents: <div>{plate.barcode?.value}</div>},
        {
          key: `${plate.id}:name`,
          contents: <div>{plate.name}</div>,
          contentsClassName: css(reorderableSimpleTableStyles.wrapText),
          cellClassName: css(reorderableSimpleTableStyles.wideColumn),
        },
        {
          key: `${plate.id}:location`,
          contents: (
            <div>
              {plate.parentStorable && !isInaccessible(plate.parentStorable) ? plate.parentStorable.name : ''}
            </div>
          ),
        },
        {
          key: `${plate.id}:modifiedAt`,
          contents: <div>{DateUtil.readableTime(plate.modifiedAt, {showTime: false})}</div>,
        },
      ],
    }));
  }

  return (
    <BenchlingQuery<ListPlates, ListPlatesVariables>
      query={LIST_PLATES_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      variables={{plateIds: props.itemIds}}
    >
      {(data) =>
        data.plates.some((plate) => isInaccessible(plate)) ? (
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="You cannot change the order of this worklist because it contains items that are inaccessible to you."
          />
        ) : (
          <ReorderableSimpleTable
            headerCells={[
              {key: 'barcode', contents: <div>Barcode</div>},
              {
                key: 'name',
                contents: <div>Name</div>,
                cellClassName: css(reorderableSimpleTableStyles.wideColumn),
              },
              {key: 'location', contents: <div>Location</div>},
              {key: 'modifiedAt', contents: <div>Modified</div>},
            ]}
            isDraggingDisabled={false}
            noRowsDisplay="Worklist is empty"
            onReorderRow={props.onReorderRow}
            rows={getPlateRows(data.plates)}
          />
        )
      }
    </BenchlingQuery>
  );
}
